import axios from 'axios'
import { API_BASE_URL } from 'config/api'
import {
  getValidAccessToken,
  isHttpAuthError,
  clearAuthCookie,
} from './tokenManager'

async function client(
  endpoint: string,
  { data, userToken, headers: customHeaders, ...customConfig }: any = {},
) {
  // Use provided token or get valid token automatically
  let token = userToken
  if (!token) {
    try {
      token = await getValidAccessToken()
    } catch (error) {
      // If no valid token and none provided, make request without auth
      // This allows for public endpoints
      token = null
    }
  }

  const config = {
    method: data ? 'POST' : 'GET',
    data: data ? data : undefined,
    url: `${API_BASE_URL}/${endpoint}`,
    headers: {
      Authorization: token ? `bearer ${token}` : undefined,
      ...customHeaders,
    },
    ...customConfig,
  }

  try {
    const response = await axios(config)
    return response.data
  } catch (error) {
    // Handle specific error cases
    if (isHttpAuthError(error)) {
      // If we used a provided token (not auto-refreshed), reject immediately
      if (userToken) {
        return Promise.reject({ message: 'Please re-authenticate.' })
      }

      // Try to refresh token and retry the request once
      try {
        const refreshedToken = await getValidAccessToken()
        if (refreshedToken && refreshedToken !== token) {
          const retryConfig = {
            ...config,
            headers: {
              ...config.headers,
              Authorization: `bearer ${refreshedToken}`,
            },
          }

          const retryResponse = await axios(retryConfig)
          return retryResponse.data
        }
      } catch (refreshError) {
        console.error('Token refresh retry failed:', refreshError)
      }

      // If refresh failed or didn't help, clear tokens and reject
      clearAuthCookie()
      return Promise.reject({ message: 'Please re-authenticate.' })
    }

    // Handle other HTTP errors
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      typeof (error as any).response === 'object'
    ) {
      const { status, data: errorData } = (error as any).response

      if (status === 401) {
        // Unauthorized - clear tokens
        clearAuthCookie()
        return Promise.reject({
          message: 'Authentication failed. Please log in again.',
        })
      }

      // Return other errors as-is
      return Promise.reject(errorData || { message: 'Request failed' })
    }

    // Handle network errors
    console.error('Network error:', error)
    return Promise.reject({
      message: 'Network error. Please check your connection.',
    })
  }
}

export { client }
